import React, { useMemo, memo, useRef, useCallback, useState, useEffect, useLayoutEffect } from 'react';
import { VariableSizeGrid as Grid } from 'react-window';
import { Box, TableSortLabel } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMatrixScale from '../hooks/useMatrixScale';
import useThrottledValue from '../hooks/useThrottledValue';
import useLiveMatrixData from '../hooks/useLiveMatrixData';
import { formatTuple, CELL_TEXT_SX, staleTickers, staleTitle, STALE_TICKER_COLOR } from '../utils/matrixFormat';
import { medianRowPercentile, medPercDelta, formatDelta, deltaColor, deltaTitle } from '../utils/medPerc';
import {
  pairZ, dollarZ, medianRowZ, cheapAndRank, yieldBasis, compareNullsLast,
  formatZ, formatCheapBp, formatRank, medZTitle, cheapTitle, rankTitle, coverageTitle,
} from '../utils/matrixStats';

// ----------------------------------------------------------------------------
// Cell flash CSS — injected once.
//
// Why so prominent: prior version was a 0.8s inset white box-shadow which is
// nearly invisible against the colored cell background. This uses a thick
// outline + glow + a slow 1.6s fade so the human eye can actually catch it.
// `up` (green) for value increases, `down` (red) for decreases.
// ----------------------------------------------------------------------------
if (typeof document !== 'undefined' && !document.getElementById('vsm-flash-style')) {
  const style = document.createElement('style');
  style.id = 'vsm-flash-style';
  style.textContent = `
    @keyframes vsm-flash-up {
      0%   { box-shadow: inset 0 0 0 3px #00e676, 0 0 12px rgba(0,230,118,0.85); }
      50%  { box-shadow: inset 0 0 0 3px #00e676, 0 0 8px rgba(0,230,118,0.55); }
      100% { box-shadow: inset 0 0 0 0 rgba(0,230,118,0), 0 0 0 rgba(0,230,118,0); }
    }
    @keyframes vsm-flash-down {
      0%   { box-shadow: inset 0 0 0 3px #ff1744, 0 0 12px rgba(255,23,68,0.85); }
      50%  { box-shadow: inset 0 0 0 3px #ff1744, 0 0 8px rgba(255,23,68,0.55); }
      100% { box-shadow: inset 0 0 0 0 rgba(255,23,68,0), 0 0 0 rgba(255,23,68,0); }
    }
    .vsm-flash-up   { animation: vsm-flash-up   1.6s ease-out; }
    .vsm-flash-down { animation: vsm-flash-down 1.6s ease-out; }
  `;
  document.head.appendChild(style);
}

// Custom hook to get container dimensions
const useContainerSize = (containerRef) => {
  const [size, setSize] = useState({ width: 800, height: 450 });

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setSize({ width: clientWidth || 800, height: clientHeight || 450 });
      }
    };

    // Initial size
    updateSize();

    // Use ResizeObserver for dynamic updates
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [containerRef]);

  return size;
};

// Cell dimensions come from useMatrixScale (see hooks/useMatrixScale.js), which
// derives them from viewport size and pixel ratio. They are passed down through
// the grid's itemData as `metrics` rather than read as module constants, so a
// resize actually re-sizes the grid.

// Color palettes (same as SpreadMatrix)
const GREEN_PALETTE = ['#1a6b05', '#2d8a0e', '#47a825', '#6bc44a', '#98d977'];
const RED_PALETTE = ['#f5c4c4', '#e99a9a', '#d66a6a', '#c43d3d', '#8c1414'];
const NEUTRAL = '#f5f5f5';

const getColorForPercentile = (percentile, isDarkMode = false) => {
  if (percentile === null || percentile === undefined) {
    return isDarkMode ? '#2a2a2a' : '#ffffff';
  }
  const p = Math.max(0, Math.min(100, percentile));
  if (p >= 48 && p <= 52) return NEUTRAL;
  if (p < 50) {
    const idx = Math.min(Math.floor(p / 10), GREEN_PALETTE.length - 1);
    return GREEN_PALETTE[idx];
  } else {
    const idx = Math.min(Math.floor((p - 50) / 10), RED_PALETTE.length - 1);
    return RED_PALETTE[idx];
  }
};

// Format spread value for display (with $ / bp / % suffixes)
const formatSpread = (value, spreadType) => {
  if (value === null || value === undefined) return '-';
  switch (spreadType) {
    case 'dollar':
      return `$${value.toFixed(2)}`;
    case 'percentile':
      return `${value.toFixed(0)}%`;
    case 'ytc':
    case 'spot-yield':
      return `${(value * 100).toFixed(0)}bp`;
    default:
      return value.toFixed(2);
  }
};

// The (p5, mean, p95) tuple is formatted by formatTuple() in utils/matrixFormat.

// Get spread field based on type
const getSpreadField = (cellData, spreadType) => {
  if (!cellData) return null;
  switch (spreadType) {
    case 'dollar':
    case 'percentile':
      return cellData.dollar_spread;
    case 'ytc':
      return cellData.ytc_spread;
    case 'spot-yield':
      return cellData.ytm_spread || cellData.spot_yield_spread || cellData.yield_spread;
    default:
      return cellData.dollar_spread;
  }
};

// Matrix cell renderer (only for columns 4+, the actual spread matrix)
const MatrixCell = memo(({
  columnIndex,
  rowIndex,
  style,
  data
}) => {
  const {
    tickers,
    matrixData,
    spreadType,
    isDarkMode,
    onCellClick,
    searchRow,
    searchCol,
    metrics,
  } = data;

  const ticker1 = tickers[rowIndex];
  const ticker2 = tickers[columnIndex]; // columnIndex is already 0-based for matrix
  const isIdentical = ticker1 === ticker2;
  const cellData = matrixData[ticker1]?.[ticker2];
  const spreadField = getSpreadField(cellData, spreadType);
  const percentile = spreadField?.percentile ?? null;
  const current = spreadField?.current;
  const p5 = spreadField?.p5;
  // The middle of the triple is the window MEAN, as in the notebook (not the 50th percentile).
  const pMid = spreadField?.avg;
  const p95 = spreadField?.p95;
  // Compact form ("-.62" not "-0.62") so the tuple fits the cell instead of
  // spilling across the border into its neighbours.
  const tupleStr = formatTuple(p5, pMid, p95, spreadType);

  const isRowHighlighted = searchRow === ticker1;
  const isColHighlighted = searchCol === ticker2;
  const isCellHighlighted = isRowHighlighted && isColHighlighted;

  const backgroundColor = isIdentical
    ? (isDarkMode ? '#616161' : '#e0e0e0')
    : getColorForPercentile(percentile, isDarkMode);

  const displayValue = spreadType === 'percentile' ? percentile : current;

  return (
    <div
      style={{
        ...style,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor,
        borderRight: '1px solid #ddd',
        borderBottom: '1px solid #ddd',
        cursor: isIdentical ? 'default' : 'pointer',
        fontSize: `${metrics.fontMain}px`,
        color: '#1a1a1a',
        // Clip at the cell box. Without this, any text wider than the cell is
        // painted straight over the neighbouring cells.
        overflow: 'hidden',
        ...(isCellHighlighted && {
          outline: '3px solid #1976d2',
          outlineOffset: '-3px',
          boxShadow: '0 0 8px rgba(25, 118, 210, 0.6)',
        }),
      }}
      onClick={isIdentical ? undefined : () => onCellClick(ticker1, ticker2)}
      data-row={ticker1}
      data-col={ticker2}
    >
      {isIdentical ? '-' : (
        <>
          {/* Top: (p5,mean,p95) numbers only */}
          <span style={{ ...CELL_TEXT_SX, fontSize: `${metrics.fontSub}px`, opacity: 0.7, lineHeight: 1.1 }}>
            {tupleStr}
          </span>
          {/* Middle: the headline value */}
          <span style={{ ...CELL_TEXT_SX, lineHeight: 1.1 }}>
            {formatSpread(displayValue, spreadType)}
          </span>
          {/* Bottom: current percentile on its own line — appended to the value
              it did not fit, and got clipped mid-number. */}
          {spreadType !== 'percentile' && percentile !== null && (
            <span style={{ ...CELL_TEXT_SX, fontSize: `${metrics.fontSub}px`, opacity: 0.7, lineHeight: 1.1 }}>
              ({percentile.toFixed(1)}%)
            </span>
          )}
        </>
      )}
    </div>
  );
});

// Sticky left columns (Ticker, Price, Strip, Med%, ΔMed, Med Z, Cheap bp, Rank) - rendered as overlay
const StickyLeftColumns = memo(({
  metrics,
  accrualByTicker,
  tickers,
  livePrices,
  matrixData,
  spreadType,
  isDarkMode,
  scrollTop,
  gridHeight,
  searchRow,
  getMedianPercentile,
  getMedianZ,
  cheapRank,
  basis,
  coverageByTicker,
  referenceMedPerc,
  referenceSession,
  staleSinceByTicker,
}) => {
  const { cellH, headerH, rowHeaderW, summaryW, stickyColsW, fontMain, fontHeader } = metrics;
  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        top: headerH,
        width: stickyColsW,
        height: gridHeight,
        overflow: 'hidden',
        zIndex: 5,
        backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
        borderRight: '2px solid #999',
      }}
    >
      <div
        style={{
          transform: `translateY(${-scrollTop}px)`,
          willChange: 'transform',
        }}
      >
        {tickers.map((ticker, rowIndex) => {
          const priceData = livePrices[ticker];
          const medianPercentile = getMedianPercentile(ticker);
          const delta = medPercDelta(medianPercentile, referenceMedPerc?.[ticker]);
          const medZ = getMedianZ(ticker);
          const cheapBp = cheapRank.cheapBp[ticker] ?? null;
          const rank = cheapRank.rank[ticker] ?? null;
          const isHighlighted = searchRow === ticker;

          return (
            <div
              key={ticker}
              style={{
                display: 'flex',
                height: cellH,
              }}
            >
              {/* Ticker */}
              <div
                data-ticker-header={ticker}
                title={[staleSinceByTicker[ticker] ? staleTitle(ticker, staleSinceByTicker[ticker]) : null, coverageByTicker[ticker]].filter(Boolean).join('\n') || undefined}
                style={{
                  width: rowHeaderW,
                  minWidth: rowHeaderW,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDarkMode ? '#424242' : '#f5f5f5',
                  borderRight: '2px solid #ccc',
                  borderBottom: '1px solid #ddd',
                  fontWeight: 'bold',
                  fontSize: `${fontMain}px`,
                  ...(staleSinceByTicker[ticker] && { color: STALE_TICKER_COLOR, fontStyle: 'italic' }),
                  ...(isHighlighted && {
                    outline: '2px solid #1976d2',
                    outlineOffset: '-2px',
                    backgroundColor: 'rgba(25, 118, 210, 0.15)',
                  }),
                }}
              >
                {ticker}
              </div>
              {/* Price */}
              <div
                data-flash-cell={`price-${ticker}`}
                title={priceData
                  ? `${ticker}\nPrice: $${priceData.price?.toFixed(2) || '-'}${priceData.kind ? ` (${priceData.kind}${priceData.kindDate ? ` ${priceData.kindDate}` : ''})` : ''}\nYield: ${priceData.currentYield?.toFixed(2) || '-'}%\nYTM: ${priceData.ytm?.toFixed(2) || '-'}%\nYTC: ${priceData.ytc?.toFixed(2) || '-'}%`
                  : ticker}
                style={{
                  width: summaryW,
                  minWidth: summaryW,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDarkMode ? '#333' : '#fafafa',
                  borderRight: '1px solid #ddd',
                  borderBottom: '1px solid #ddd',
                  fontSize: `${fontHeader}px`,
                }}
              >
                {priceData?.price ? priceData.price.toFixed(2) : '-'}
              </div>
              {/* Strip */}
              <div
                data-flash-cell={`strip-${ticker}`}
                style={{
                  width: summaryW,
                  minWidth: summaryW,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDarkMode ? '#333' : '#fafafa',
                  borderRight: '1px solid #ddd',
                  borderBottom: '1px solid #ddd',
                  fontSize: `${fontHeader}px`,
                }}
              >
                {(() => {
                  const acc = priceData?.accrual ?? accrualByTicker?.[ticker];
                  const strip = (priceData?.price != null && acc != null)
                    ? priceData.price - acc
                    : priceData?.cleanPrice;
                  return strip != null ? strip.toFixed(2) : '-';
                })()}
              </div>
              {/* Med% */}
              <div
                data-flash-cell={`medperc-${ticker}`}
                style={{
                  width: summaryW,
                  minWidth: summaryW,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: getColorForPercentile(medianPercentile, isDarkMode),
                  borderRight: '1px solid #ddd',
                  borderBottom: '1px solid #ddd',
                  fontSize: `${fontHeader}px`,
                  fontWeight: 'bold',
                  color: '#1a1a1a',
                }}
              >
                {medianPercentile !== null ? `${medianPercentile.toFixed(2)}%` : '-'}
              </div>
              {/* Δ Med% vs the reference close */}
              <div
                title={deltaTitle(referenceSession)}
                style={{
                  width: summaryW,
                  minWidth: summaryW,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDarkMode ? '#333' : '#fafafa',
                  borderRight: '1px solid #ddd',
                  borderBottom: '1px solid #ddd',
                  fontSize: `${fontHeader}px`,
                  fontWeight: 'bold',
                  color: deltaColor(delta, isDarkMode),
                }}
              >
                {referenceSession ? formatDelta(delta) : '\u2026'}
              </div>
              {/* Med Z: median over peers of each pair's z-score (notebook med-z) */}
              <div
                title={medZTitle(spreadType)}
                style={{
                  width: summaryW,
                  minWidth: summaryW,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDarkMode ? '#333' : '#fafafa',
                  borderRight: '1px solid #ddd',
                  borderBottom: '1px solid #ddd',
                  fontSize: `${fontHeader}px`,
                  fontWeight: 'bold',
                }}
              >
                {formatZ(medZ)}
              </div>
              {/* Cheap bp: today's yield minus its own historical mean (notebook cheap-bp) */}
              <div
                title={cheapTitle(basis)}
                style={{
                  width: summaryW,
                  minWidth: summaryW,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDarkMode ? '#333' : '#fafafa',
                  borderRight: '1px solid #ddd',
                  borderBottom: '1px solid #ddd',
                  fontSize: `${fontHeader}px`,
                  fontWeight: 'bold',
                  // Positive = cheaper = the green side, the opposite of a rising Med%.
                  color: deltaColor(cheapBp === null ? null : -cheapBp / 100, isDarkMode),
                }}
              >
                {formatCheapBp(cheapBp)}
              </div>
              {/* Rank by Cheap bp, 1 = cheapest (notebook rank) */}
              <div
                title={rankTitle(basis)}
                style={{
                  width: summaryW,
                  minWidth: summaryW,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDarkMode ? '#333' : '#fafafa',
                  borderRight: '2px solid #ccc',
                  borderBottom: '1px solid #ddd',
                  fontSize: `${fontHeader}px`,
                  fontWeight: 'bold',
                }}
              >
                {formatRank(rank)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

// Sticky header for left columns (corner piece)
const StickyCornerHeader = memo(({
  metrics,
  isDarkMode,
  sortBy,
  sortDirection,
  onSort,
  referenceSession,
  spreadType,
  basis,
}) => {
  const { headerH, rowHeaderW, summaryW, stickyColsW, fontMain, fontHeader } = metrics;
  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: stickyColsW,
        height: headerH,
        display: 'flex',
        backgroundColor: isDarkMode ? '#424242' : '#f5f5f5',
        borderBottom: '2px solid #ccc',
        borderRight: '2px solid #999',
        zIndex: 15, // Above both sticky columns and scrolling header
      }}
    >
      {/* Ticker header */}
      <div style={{
        width: rowHeaderW,
        minWidth: rowHeaderW,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRight: '2px solid #ccc',
        fontWeight: 'bold',
        fontSize: `${fontMain}px`,
      }}>
        Ticker
      </div>
      {/* Price header */}
      <div
        style={{
          width: summaryW,
          minWidth: summaryW,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRight: '1px solid #ddd',
          fontWeight: 'bold',
          fontSize: `${fontHeader}px`,
          cursor: 'pointer',
        }}
        onClick={() => onSort('price')}
      >
        <TableSortLabel
          active={sortBy === 'price'}
          direction={sortBy === 'price' ? sortDirection : 'desc'}
          sx={{ '& .MuiTableSortLabel-icon': { fontSize: `${fontHeader * 1.15}px` } }}
        >
          Price
        </TableSortLabel>
      </div>
      {/* Strip header */}
      <div
        style={{
          width: summaryW,
          minWidth: summaryW,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRight: '1px solid #ddd',
          fontWeight: 'bold',
          fontSize: `${fontHeader}px`,
          cursor: 'pointer',
        }}
        onClick={() => onSort('strip')}
      >
        <TableSortLabel
          active={sortBy === 'strip'}
          direction={sortBy === 'strip' ? sortDirection : 'desc'}
          sx={{ '& .MuiTableSortLabel-icon': { fontSize: `${fontHeader * 1.15}px` } }}
        >
          Strip
        </TableSortLabel>
      </div>
      {/* Med% header */}
      <div
        style={{
          width: summaryW,
          minWidth: summaryW,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRight: '1px solid #ddd',
          fontWeight: 'bold',
          fontSize: `${fontHeader}px`,
          cursor: 'pointer',
        }}
        onClick={() => onSort('medPerc')}
      >
        <TableSortLabel
          active={sortBy === 'medPerc'}
          direction={sortBy === 'medPerc' ? sortDirection : 'desc'}
          sx={{ '& .MuiTableSortLabel-icon': { fontSize: `${fontHeader * 1.15}px` } }}
        >
          Med%
        </TableSortLabel>
      </div>
      {/* Δ Med% header */}
      <div
        title={deltaTitle(referenceSession)}
        style={{
          width: summaryW,
          minWidth: summaryW,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRight: '1px solid #ddd',
          fontWeight: 'bold',
          fontSize: `${fontHeader}px`,
          cursor: 'pointer',
        }}
        onClick={() => onSort('deltaMed')}
      >
        <TableSortLabel
          active={sortBy === 'deltaMed'}
          direction={sortBy === 'deltaMed' ? sortDirection : 'desc'}
          sx={{ '& .MuiTableSortLabel-icon': { fontSize: `${fontHeader * 1.15}px` } }}
        >
          ΔMed
        </TableSortLabel>
      </div>
      {/* Med Z header */}
      <div
        title={medZTitle(spreadType)}
        style={{
          width: summaryW,
          minWidth: summaryW,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRight: '1px solid #ddd',
          fontWeight: 'bold',
          fontSize: `${fontHeader}px`,
          cursor: 'pointer',
        }}
        onClick={() => onSort('medZ')}
      >
        <TableSortLabel
          active={sortBy === 'medZ'}
          direction={sortBy === 'medZ' ? sortDirection : 'desc'}
          sx={{ '& .MuiTableSortLabel-icon': { fontSize: `${fontHeader * 1.15}px` } }}
        >
          MedZ
        </TableSortLabel>
      </div>
      {/* Cheap bp header */}
      <div
        title={cheapTitle(basis)}
        style={{
          width: summaryW,
          minWidth: summaryW,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRight: '1px solid #ddd',
          fontWeight: 'bold',
          fontSize: `${fontHeader}px`,
          cursor: 'pointer',
        }}
        onClick={() => onSort('cheap')}
      >
        <TableSortLabel
          active={sortBy === 'cheap'}
          direction={sortBy === 'cheap' ? sortDirection : 'desc'}
          sx={{ '& .MuiTableSortLabel-icon': { fontSize: `${fontHeader * 1.15}px` } }}
        >
          Cheap
        </TableSortLabel>
      </div>
      {/* Rank header */}
      <div
        title={rankTitle(basis)}
        style={{
          width: summaryW,
          minWidth: summaryW,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRight: '2px solid #ccc',
          fontWeight: 'bold',
          fontSize: `${fontHeader}px`,
          cursor: 'pointer',
        }}
        onClick={() => onSort('rank')}
      >
        <TableSortLabel
          active={sortBy === 'rank'}
          direction={sortBy === 'rank' ? sortDirection : 'desc'}
          sx={{ '& .MuiTableSortLabel-icon': { fontSize: `${fontHeader * 1.15}px` } }}
        >
          Rank
        </TableSortLabel>
      </div>
    </div>
  );
});

// Scrolling header for matrix columns (ticker names at top)
const MatrixColumnHeaders = memo(({
  metrics,
  tickers,
  scrollLeft,
  isDarkMode,
  searchCol,
}) => {
  const { cellW, headerH, stickyColsW, fontHeader } = metrics;
  const numTickers = tickers.length;
  const totalWidth = numTickers * cellW;

  return (
    <div
      style={{
        position: 'absolute',
        left: stickyColsW,
        top: 0,
        right: 0,
        height: headerH,
        overflow: 'hidden',
        backgroundColor: isDarkMode ? '#424242' : '#f5f5f5',
        borderBottom: '2px solid #ccc',
        zIndex: 10,
      }}
    >
      <div
        style={{
          display: 'flex',
          width: totalWidth,
          transform: `translateX(${-scrollLeft}px)`,
          willChange: 'transform',
        }}
      >
        {tickers.map((ticker) => {
          const isHighlighted = searchCol === ticker;
          return (
            <div
              key={ticker}
              style={{
                width: cellW,
                minWidth: cellW,
                height: headerH,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: '1px solid #ddd',
                fontWeight: 'bold',
                fontSize: `${fontHeader}px`,
                ...(isHighlighted && {
                  outline: '2px solid #1976d2',
                  outlineOffset: '-2px',
                  backgroundColor: 'rgba(25, 118, 210, 0.15)',
                }),
              }}
            >
              {ticker}
            </div>
          );
        })}
      </div>
    </div>
  );
});

const VirtualizedSpreadMatrix = ({
  tickers,
  matrix,
  granularity,
  spreadType = 'percentile',
  onCellClick,
  livePrices: rawLivePrices = {},
  yields = null,     // per-ticker { price, clean_price, accrual } from the matrix payload
  searchRow = null,
  searchCol = null,
  referenceMedPerc = null,  // ticker -> Med% at the reference close, for the active spread type
  referenceSession = null,
  getScaleFactor = null,    // (t1, t2) => number, for dollar spread scaling A - s*B
}) => {
  // Prices arrive several times a second; recomputing 10k+ cells that often is
  // pure waste for updates nobody can read. One update per second is plenty.
  const livePrices = useThrottledValue(rawLivePrices, 1000);

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const gridRef = useRef(null);
  const containerRef = useRef(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  // Automatic sizing from viewport + pixel ratio.
  const metrics = useMatrixScale();

  // Accrued dividend per ticker (daily data from the matrix payload). The live
  // WebSocket feed carries `price` but not `cleanPrice`, so Strip is derived as
  // live price - accrual rather than read from a field that never ticks.
  const accrualByTicker = useMemo(() => {
    const out = {};
    if (!yields) return out;
    for (const t of Object.keys(yields)) {
      const y = yields[t];
      if (!y) continue;
      out[t] = y.accrual != null
        ? y.accrual
        : (y.price != null && y.clean_price != null ? y.price - y.clean_price : null);
    }
    return out;
  }, [yields]);
  const { cellW, cellH, headerH, stickyColsW } = metrics;
  const staleSinceByTicker = useMemo(() => staleTickers(yields), [yields]);

  // Notebook columns: cheap-bp and rank come from the live yields and each
  // ticker's historical mean (matrix payload); the $ view measures them on YTC.
  const basis = yieldBasis(spreadType);
  const cheapRank = useMemo(
    () => cheapAndRank(tickers, livePrices, yields, basis),
    [tickers, livePrices, yields, basis]
  );
  const coverageByTicker = useMemo(() => {
    const out = {};
    if (yields) for (const t of Object.keys(yields)) out[t] = coverageTitle(yields[t]?.hist);
    return out;
  }, [yields]);

  // react-window caches column/row geometry, so it must be told when the scale
  // changes or it keeps rendering at the old cell size.
  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.resetAfterIndices({ columnIndex: 0, rowIndex: 0, shouldForceUpdate: true });
    }
  }, [cellW, cellH]);

  // Sorting state
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');

  // Live values, YTC/YTM percentiles from the curve, and per-pair ratios —
  // the same computation as the table (see hooks/useLiveMatrixData).
  const { matrixData } = useLiveMatrixData(tickers, matrix, livePrices, getScaleFactor);

  // Calculate median percentile for a ticker — matches notebook np.median(current_perc.iloc[i])
  // Notebook diagonal: spread[i][i] is all-zeros series; percentileofscore(zeros, 0) = 50
  const getMedianPercentile = useCallback(
    (ticker) => medianRowPercentile(
      ticker, tickers, matrixData[ticker],
      (cell) => getSpreadField(cell, spreadType)?.percentile ?? null
    ),
    [tickers, matrixData, spreadType]
  );

  // Median over peers of each pair's z-score: yield views from the pair's
  // mean/std and the live spread, the $ view from the server's synthetic-series z.
  const getMedianZ = useCallback(
    (ticker) => medianRowZ(
      ticker, tickers, matrixData[ticker],
      (cell) => (spreadType === 'dollar' || spreadType === 'percentile'
        ? dollarZ(cell?.dollar_spread)
        : pairZ(getSpreadField(cell, spreadType)))
    ),
    [tickers, matrixData, spreadType]
  );

  // ------------------------------------------------------------------
  // Cell flash on value change
  // ------------------------------------------------------------------
  // After every matrixData / livePrices update, walk currently-rendered cells
  // (react-window only mounts visible ones, which is exactly what we care
  // about) and flash any whose displayed value changed. Direction (up/down)
  // chooses color. We compare the "current" field of whichever spread type
  // is active so the flash matches what the user actually sees.
  const prevCellValuesRef = useRef({});
  useEffect(() => {
    const prev = prevCellValuesRef.current;
    const next = {};
    const root = containerRef.current;
    if (!root) {
      prevCellValuesRef.current = next;
      return;
    }
    const cssEscape = (s) => (window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/"/g, '\\"'));
    const flashEl = (el, prevValue, value) => {
      if (!el || prevValue == null || prevValue === value) return;
      const cls = value > prevValue ? 'vsm-flash-up' : 'vsm-flash-down';
      el.classList.remove('vsm-flash-up', 'vsm-flash-down');
      // Force reflow so the animation restarts on rapid successive changes
      void el.offsetWidth;
      el.classList.add(cls);
    };

    // Matrix cells (off-diagonal). react-window only mounts visible cells, so
    // iterate the elements that actually exist rather than every pair: at 103
    // tickers the old loop ran 10,506 querySelector calls per price tick, all
    // but a couple of hundred of which could not match anything.
    const renderedCells = root.querySelectorAll('[data-row][data-col]');
    for (const el of renderedCells) {
      const t1 = el.getAttribute('data-row');
      const t2 = el.getAttribute('data-col');
      if (!t1 || !t2 || t1 === t2) continue;
      const cell = matrixData[t1]?.[t2];
      if (!cell) continue;
      const field = getSpreadField(cell, spreadType);
      const value = spreadType === 'percentile' ? field?.percentile : field?.current;
      if (value == null || isNaN(value)) continue;
      const key = `mx|${t1}|${t2}`;
      next[key] = value;
      flashEl(el, prev[key], value);
    }

    // Sticky-left columns: price, strip (clean price), and med-percentile per ticker
    for (const ticker of tickers) {
      const priceData = livePrices[ticker];
      const checks = [
        ['price', priceData?.price],
        ['strip', priceData?.cleanPrice],
        ['medperc', getMedianPercentile(ticker)]
      ];
      for (const [label, value] of checks) {
        if (value == null || isNaN(value)) continue;
        const key = `${label}|${ticker}`;
        next[key] = value;
        const el = root.querySelector(`[data-flash-cell="${label}-${cssEscape(ticker)}"]`);
        flashEl(el, prev[key], value);
      }
    }

    prevCellValuesRef.current = next;
  }, [matrixData, tickers, spreadType, livePrices, getMedianPercentile]);

  // Sort tickers
  const sortedTickers = useMemo(() => {
    if (!sortBy) return tickers;
    return [...tickers].sort((a, b) => {
      let valA, valB;
      switch (sortBy) {
        case 'price':
          valA = livePrices[a]?.price ?? 0;
          valB = livePrices[b]?.price ?? 0;
          break;
        case 'strip':
          valA = livePrices[a]?.cleanPrice ?? 0;
          valB = livePrices[b]?.cleanPrice ?? 0;
          break;
        case 'medPerc':
          valA = getMedianPercentile(a) ?? 50;
          valB = getMedianPercentile(b) ?? 50;
          break;
        case 'deltaMed':
          valA = medPercDelta(getMedianPercentile(a), referenceMedPerc?.[a]) ?? 0;
          valB = medPercDelta(getMedianPercentile(b), referenceMedPerc?.[b]) ?? 0;
          break;
        // The notebook columns: rows without a value stay at the bottom either way.
        case 'medZ':
          return compareNullsLast(getMedianZ(a), getMedianZ(b), sortDirection);
        case 'cheap':
          return compareNullsLast(cheapRank.cheapBp[a], cheapRank.cheapBp[b], sortDirection);
        case 'rank':
          // Descending shows rank 1 first, the same order as Cheap bp descending.
          return compareNullsLast(
            cheapRank.rank[a] == null ? null : -cheapRank.rank[a],
            cheapRank.rank[b] == null ? null : -cheapRank.rank[b],
            sortDirection
          );
        default:
          return 0;
      }
      return sortDirection === 'asc' ? valA - valB : valB - valA;
    });
  }, [tickers, sortBy, sortDirection, livePrices, getMedianPercentile, referenceMedPerc, getMedianZ, cheapRank]);

  const handleSort = useCallback((column) => {
    if (sortBy === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
  }, [sortBy]);

  // Scroll to highlighted cell
  useEffect(() => {
    if (searchRow && searchCol && gridRef.current) {
      const rowIndex = sortedTickers.indexOf(searchRow);
      const colIndex = sortedTickers.indexOf(searchCol);
      if (rowIndex >= 0 && colIndex >= 0) {
        gridRef.current.scrollToItem({
          rowIndex,
          columnIndex: colIndex, // Matrix grid is 0-indexed for matrix columns only
          align: 'center',
        });
      }
    }
  }, [searchRow, searchCol, sortedTickers]);

  // Handle scroll sync for both header and sticky columns
  const handleScroll = useCallback(({ scrollLeft: newScrollLeft, scrollTop: newScrollTop }) => {
    setScrollLeft(newScrollLeft);
    setScrollTop(newScrollTop);
  }, []);

  const handleCellClick = useCallback((t1, t2) => {
    if (onCellClick) onCellClick(t1, t2);
  }, [onCellClick]);

  const numTickers = sortedTickers.length;

  // Item data for matrix cells only
  const itemData = useMemo(() => ({
    tickers: sortedTickers,
    matrixData,
    spreadType,
    isDarkMode,
    onCellClick: handleCellClick,
    searchRow,
    searchCol,
    metrics,
  }), [sortedTickers, matrixData, spreadType, isDarkMode, handleCellClick, searchRow, searchCol, metrics]);

  // Get dynamic container size
  const containerSize = useContainerSize(containerRef);
  const gridHeight = Math.max(containerSize.height - headerH, 200);
  // Width available for the matrix grid (subtract sticky columns)
  const matrixGridWidth = Math.max(containerSize.width - stickyColsW, 200);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',  // Fill parent container
        minHeight: 300,
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
        border: '1px solid',
        borderColor: isDarkMode ? 'grey.700' : 'grey.300',
        borderRadius: 1,
      }}
    >
      {/* Sticky corner header (Ticker, Price, Strip, Med%, ΔMed, Med Z, Cheap bp, Rank headers) */}
      <StickyCornerHeader
        metrics={metrics}
        isDarkMode={isDarkMode}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={handleSort}
        referenceSession={referenceSession}
        spreadType={spreadType}
        basis={basis}
      />

      {/* Scrolling matrix column headers */}
      <MatrixColumnHeaders
        metrics={metrics}
        tickers={sortedTickers}
        scrollLeft={scrollLeft}
        isDarkMode={isDarkMode}
        searchCol={searchCol}
      />

      {/* Sticky left columns (Ticker, Price, Strip, Med%, ΔMed, Med Z, Cheap bp, Rank) */}
      <StickyLeftColumns
        metrics={metrics}
        accrualByTicker={accrualByTicker}
        tickers={sortedTickers}
        livePrices={livePrices}
        matrixData={matrixData}
        spreadType={spreadType}
        isDarkMode={isDarkMode}
        scrollTop={scrollTop}
        gridHeight={gridHeight}
        searchRow={searchRow}
        getMedianPercentile={getMedianPercentile}
        getMedianZ={getMedianZ}
        cheapRank={cheapRank}
        basis={basis}
        coverageByTicker={coverageByTicker}
        referenceMedPerc={referenceMedPerc}
        referenceSession={referenceSession}
        staleSinceByTicker={staleSinceByTicker}
      />

      {/* Virtualized matrix grid (only the spread cells) */}
      <div
        style={{
          position: 'absolute',
          left: stickyColsW,
          top: headerH,
          right: 0,
          bottom: 0,
        }}
      >
        <Grid
          ref={gridRef}
          columnCount={numTickers}
          columnWidth={() => cellW}
          height={gridHeight}
          rowCount={numTickers}
          rowHeight={() => cellH}
          width={matrixGridWidth}
          itemData={itemData}
          onScroll={handleScroll}
          style={{ outline: 'none' }}
          overscanRowCount={5}
          overscanColumnCount={3}
        >
          {MatrixCell}
        </Grid>
      </div>
    </Box>
  );
};

export default memo(VirtualizedSpreadMatrix);
